import { z } from 'zod'

/** Pitch class 0..11, C = 0. */
export const Pc = z.number().int().min(0).max(11)
export type Pc = z.infer<typeof Pc>

/** Letter degree 0..6 (I..VII): the letter a numeral is written with (spec §1). */
export const Degree = z.number().int().min(0).max(6)
export type Degree = z.infer<typeof Degree>

export const SCALE_IDS = [
  'ionian',
  'dorian',
  'phrygian',
  'lydian',
  'mixolydian',
  'aeolian',
] as const
export const ScaleId = z.enum(SCALE_IDS)
export type ScaleId = z.infer<typeof ScaleId>

/** The two observatories that chart the one sky (spec §1); a song belongs to its mode's parent's. */
export const OBSERVATORIES = ['major', 'minor'] as const
export const Observatory = z.enum(OBSERVATORIES)
export type Observatory = z.infer<typeof Observatory>

export const Quality = z.enum(['maj', 'min', 'dim', 'aug', 'sus2', 'sus4'])
export type Quality = z.infer<typeof Quality>

export const Seventh = z.enum(['none', 'min7', 'maj7', 'dim7'])
export type Seventh = z.infer<typeof Seventh>

/** The three rings of the wheel (spec §4): a triad quality each. */
export const RINGS = ['maj', 'min', 'dim'] as const
export const Ring = z.enum(RINGS)
export type Ring = z.infer<typeof Ring>

/** The natural spokes: the major scale, the reference of every numeral (spec §1). */
export const NATURAL: readonly number[] = [0, 2, 4, 5, 7, 9, 11]

/**
 * A cell of the wheel (spec §1, §4): a root relative to the tonic and a ring, `'7:maj'` = V,
 * `'3:maj'` = ♭III, `'11:dim'` = vii°, `'0:min'` = i. The place of a chord, the unit of an answer.
 */
export type Cell = `${number}:${Ring}`
const CELL_RE = /^(?:[0-9]|1[01]):(?:maj|min|dim)$/
export const Cell = z.custom<Cell>(
  (v) => typeof v === 'string' && CELL_RE.test(v),
  'a cell of the wheel',
)

export const cellId = (rel: number, ring: Ring): Cell => `${rel}:${ring}` as Cell

export function parseCell(cell: Cell): { rel: Pc; ring: Ring } {
  const [rel, ring] = cell.split(':') as [string, Ring]
  return { rel: Number(rel) as Pc, ring }
}

/** Whether a cell stands on a chromatic spoke (drawn as a shade of its letter's colour, spec §4). */
export function isChromatic(cell: Cell): boolean {
  return !NATURAL.includes(parseCell(cell).rel)
}

/**
 * The stars of the sky (spec §5): every cell a diatonic mode occupies, and the applied dominants
 * and leading-tone chords. The remaining eight cells are dark: no night opens them, no song is
 * asked them. Listed spoke by spoke, the major ring first on each.
 */
export const STARS: readonly Cell[] = [
  '0:maj',
  '0:min',
  '1:maj',
  '1:dim',
  '2:maj',
  '2:min',
  '2:dim',
  '3:maj',
  '3:dim',
  '4:maj',
  '4:min',
  '4:dim',
  '5:maj',
  '5:min',
  '6:dim',
  '7:maj',
  '7:min',
  '7:dim',
  '8:maj',
  '8:dim',
  '9:maj',
  '9:min',
  '9:dim',
  '10:maj',
  '10:min',
  '11:maj',
  '11:min',
  '11:dim',
]
const STAR_SET: ReadonlySet<string> = new Set(STARS)

/** Whether a cell is one of the sky's stars; false for the eight dark cells. */
export const isStar = (cell: Cell): boolean => STAR_SET.has(cell)

export const AbsChord = z.object({
  root: Pc,
  quality: Quality,
  seventh: Seventh,
  bass: Pc,
})
export type AbsChord = z.infer<typeof AbsChord>

/** A chord's label (spec §2, §3): its place on the wheel and the numerals it is written with. */
export const Label = z.object({
  /** The root relative to the tonic. */
  rel: Pc,
  /** The letter its numeral is written with: colour, keyboard. */
  degree: Degree,
  /** The chord's place on the wheel; `isStar(cell)` says whether a night can open it. */
  cell: Cell,
  /** The triad's own numeral, what a star is named by: `♭III`, `vii°`, `V`. */
  own: z.string(),
  /** The label shown: the context and the seventh, `V/vi`, `I7`, `V+`, `IVsus4`. */
  text: z.string(),
  hasSeventh: z.boolean(),
})
export type Label = z.infer<typeof Label>

/** An answer is a cell, a root and a triad quality (spec §4); the seventh is colour and never asked (§6). */
export const Answer = z.object({
  rel: Pc,
  ring: Ring,
})
export type Answer = z.infer<typeof Answer>

export const cellOfAnswer = (a: Answer): Cell => cellId(a.rel, a.ring)

export const Level = z.number().int().min(1).max(7)
export type Level = z.infer<typeof Level>
